import { BaseLogger } from './base-logger';
import {
  LogLevel,
  LoggerOptions,
  LoggerTransport,
  LoggerSettings,
  Logger,
  RegisteredTransports,
  CONSOLE_TRANSPORT_NAME,
  HTTP_TRANSPORT_NAME,
  DEFAULT_SETTINGS_KEY
} from './logger.model';
import { HttpTransport } from './transports/http.transport';
import { ConsoleTransport } from './transports/console.transport';

export class WorkerLoggerService {
  private options: LoggerOptions;
  private registeredTransports: RegisteredTransports = {};
  private loggers: { [name: string]: Logger } = {};
  private activeTransports: { [name: string]: LoggerTransport } = {};
  private nullLogger: BaseLogger = new BaseLogger([], LogLevel.Error);

  constructor() {}

  public configure(options?: LoggerOptions) {
    // register the known logger transports
    this.registerTransportType(HTTP_TRANSPORT_NAME, HttpTransport);
    this.registerTransportType(CONSOLE_TRANSPORT_NAME, ConsoleTransport);

    if (!!options) {
      this.options = options;
      return;
    }

    this.options = this.getDefaultLoggerOptions();
  }

  public registerTransportType(name: string, transportType: any): void {
    this.registeredTransports[name] = transportType;
  }

  public getLogger(name: string): Logger {
    if (!this.options) {
      return this.nullLogger;
    }

    if (!!this.loggers[name]) {
      return this.loggers[name];
    }

    let settings: LoggerSettings = null;
    const segments = name.split('.');
    while (segments.length > 0) {
      const loggerSettings = this.options.settings[segments.join('.')];
      if (!!loggerSettings) {
        settings = loggerSettings;
        break;
      }

      segments.pop();
    }

    // if we did not find the settings by name, check for 'default'
    // value. if the default is not found then the null logger will be used
    if (!!!settings) {
      settings = this.options.settings[DEFAULT_SETTINGS_KEY];
    }

    let logger: Logger;
    if (!!settings) {
      // settings were found, get the transports for the logger
      const transports = this.getTransportsForLogger(settings);

      logger = new BaseLogger(transports, settings.logLevel);
    } else {
      // settings not found use "null logger"
      logger = this.nullLogger;
    }

    this.loggers[name] = logger;

    return logger;
  }

  private getTransportsForLogger(settings: LoggerSettings): LoggerTransport[] {
    const transports: LoggerTransport[] = [];

    for (let transportName of settings.transports) {
      if (!!this.activeTransports[transportName]) {
        transports.push(this.activeTransports[transportName]);
        continue;
      }

      const transportConfig = this.options.transports[transportName];

      const transportType = this.registeredTransports[transportName];
      if (!!!transportType) {
        console.error(`LoggerTransport type ${transportName} is not registered.`);
        continue;
      }

      const transport = !!transportConfig ? new transportType(transportConfig) : new transportType();
      this.activeTransports[transportName] = transport;
      transports.push(transport);
    }

    return transports;
  }

  private getDefaultLoggerOptions(): LoggerOptions {
    return {
      transports: {
        [CONSOLE_TRANSPORT_NAME]: new ConsoleTransport()
      },
      settings: {
        [DEFAULT_SETTINGS_KEY]: {
          transports: [],
          logLevel: LogLevel.Info
        }
      }
    };
  }
}
